<template>
  <div class="adaptive-review-container">
    <page-header :breadcrumbs="breadcrumbs">
      <template #topbar>
        <h1>{{ simulation?.name }}</h1>
        <improve-question
          v-if="flashcard"
          :question="flashcard"
        />
      </template>
    </page-header>
    <flashcard
      v-if="flashcard && item"
      :key="item.position"
      :answered="true"
      :answer-ids="item.answerIds || []"
      :correct="item.correct"
      :flashcard="flashcard"
      :category="simulation"
      :flashcard-state="flashcardState"
      :raw-answer-data="item.rawAnswerData"
    />
    <p
      v-else-if="loaded"
      class="missing"
    >
      This question couldn't be found.
    </p>
    <page-footer>
      <router-link
        v-if="previousItem"
        :to="questionRoute(previousItem)"
        class="previous"
      >
        <icon
          name="arrow"
          variant="a8aab7"
        />
        Previous
      </router-link>
      <router-link
        v-if="nextItem"
        :to="questionRoute(nextItem)"
        class="next"
      >
        Next
        <icon
          name="arrow"
          variant="a8aab7"
        />
      </router-link>
      <div class="progress">
        <div class="counter">
          {{ index + 1 }}/{{ items.length }}
        </div>
        <pure-progress-bar
          :answered="index + 1"
          :total="items.length"
        />
      </div>
      <router-link
        :to="resultRoute"
        class="back"
      >
        <icon
          class="list-icon"
          name="hamburger"
          variant="a8aab7"
        />Back To Results
      </router-link>
    </page-footer>
  </div>
</template>

<script>
// One question from a finished adaptive attempt, with the student's answer, the correct answer and the rationale
import Flashcard from '@components/flashcards/flashcard';
import Icon from '@components/singletons/icon';
import ImproveQuestion from '@components/singletons/improveQuestion';
import PageHeader from '@components/singletons/pageHeader';
import PageFooter from '@components/singletons/pageFooter';
import PureProgressBar from '@components/progress/PureProgressBar';
import WuxSimulationsMixin from '@/mixins/wux-simulations';

export default {
  components: {
    Flashcard, Icon, ImproveQuestion, PageFooter, PageHeader, PureProgressBar,
  },
  mixins: [WuxSimulationsMixin],
  data() {
    return { loaded: false };
  },
  computed: {
    sessionId() {
      return +this.$route.params.sessionId;
    },
    position() {
      return +this.$route.params.position;
    },
    items() {
      const review = this.$store.getters['adaptiveSim/review'];
      return review?.sessionId === this.sessionId ? review.items : [];
    },
    index() {
      return this.items.findIndex(item => item.position === this.position);
    },
    item() {
      return this.items[this.index];
    },
    previousItem() {
      return this.index > 0 ? this.items[this.index - 1] : null;
    },
    nextItem() {
      return this.index >= 0 && this.index < this.items.length - 1 ? this.items[this.index + 1] : null;
    },
    flashcard() {
      return this.item ? this.$store.getters['flashcards/flashcards'][this.item.flashcardId] : null;
    },
    flashcardState() {
      return {
        flashcardId: this.item.flashcardId,
        answerIds: this.item.answerIds || [],
        rawAnswerData: this.item.rawAnswerData,
        correct: this.item.correct,
        categoryId: this.simulation?.id,
      };
    },
    resultRoute() {
      return { name: 'adaptiveSim#result', params: { simId: this.simId, sessionId: this.sessionId } };
    },
    breadcrumbs() {
      return [
        { name: this.simulation?.name, route: this.resultRoute },
        { name: `Question ${this.position}` },
      ];
    },
  },
  created() {
    if (this.items.length) {
      this.loaded = true;
      return;
    }
    this.$store.dispatch('adaptiveSim/fetchReview', this.sessionId)
      .catch(error => {
        console.error(error);
        this.$router.replace(this.resultRoute);
      })
      .finally(() => {
        this.loaded = true;
      });
  },
  methods: {
    questionRoute(item) {
      return { name: 'adaptiveSim#review', params: { simId: this.simId, sessionId: this.sessionId, position: item.position } };
    },
  },
};
</script>

<style lang='scss' scoped>
  .adaptive-review-container {
    @include flashcard-view;

    .missing {
      text-align: center;
      margin: 2rem 0;
    }

    .page-footer-component-container {
      .progress {
        align-items: center;
        display: flex;
        flex: 1;

        .counter {
          color: #354052;
          font-size: rem-calc(12);
          font-weight: $semi-bold;
          margin-right: rem-calc(8);
        }
      }

      > a {
        @include hlt-btn-1;
        font-size: rem-calc(14);
        padding: rem-calc(6) rem-calc(12);

        &.back {
          margin-left: auto;

          @include breakpoint(small) {
            display: none;
          }
        }

        &.next {
          margin-right: rem-calc(12);

          img {
            margin: 0 0 0 rem-calc(7);
          }
        }

        &.previous {
          margin-right: rem-calc(12);

          img {
            transform: rotate(180deg);
          }
        }

        img {
          height: rem-calc(16);
          margin: 0 rem-calc(7) 0 0;
          width: rem-calc(16);
        }

        .list-icon {
          height: auto;
          margin: 0 rem-calc(7) 0 0;
          width: rem-calc(16);
        }
      }
    }
  }
</style>
